import { useCallback, useEffect, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Menú desplegable sin dependencias (el mismo patrón que usan el selector de negocio y
 * el menú de usuario del header).
 *
 * Accesibilidad cubierta: `role="menu"`, cierre con Escape y con click fuera del panel.
 * El panel se posiciona de forma absoluta respecto al trigger (`align` = lado hacia el
 * que se abre).
 */

export interface DropdownProps {
  /** Render del disparador. Recibe `open` y `toggle`. */
  trigger: (state: { open: boolean; toggle: () => void }) => ReactNode;
  /** Contenido del panel. Si es función, recibe `close` para cerrarlo al elegir. */
  children: ReactNode | ((state: { close: () => void }) => ReactNode);
  align?: 'start' | 'end';
  /** Clases del panel (ancho, etc.). */
  className?: string;
}

export function Dropdown({ trigger, children, align = 'end', className }: DropdownProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((value) => !value), []);

  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: MouseEvent | TouchEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      {trigger({ open, toggle })}

      {open ? (
        <div
          role="menu"
          className={cn(
            'absolute z-50 mt-2 min-w-56 rounded-panel border border-line bg-surface p-1.5 shadow-pop',
            align === 'end' ? 'right-0' : 'left-0',
            className,
          )}
        >
          {typeof children === 'function' ? children({ close }) : children}
        </div>
      ) : null}
    </div>
  );
}

export interface DropdownItemProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: ReactNode;
  /** Marca el ítem como la opción activa (ej. el tenant seleccionado). */
  active?: boolean;
  tone?: 'default' | 'danger';
}

export function DropdownItem({
  icon,
  active = false,
  tone = 'default',
  className,
  children,
  ...props
}: DropdownItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      className={cn(
        'flex w-full items-center gap-2.5 rounded-control px-3 py-2 text-left text-body transition-colors',
        'focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-brand-500',
        tone === 'danger'
          ? 'text-danger-strong hover:bg-danger-soft'
          : 'text-ink-soft hover:bg-surface-muted',
        active && 'bg-brand-50 font-medium text-brand-700',
        className,
      )}
      {...props}
    >
      {icon ? <span className="shrink-0 text-ink-faint">{icon}</span> : null}
      <span className="min-w-0 flex-1 truncate">{children}</span>
    </button>
  );
}

export function DropdownSeparator() {
  return <div className="my-1.5 h-px bg-line" role="separator" />;
}

export function DropdownLabel({ children }: { children: ReactNode }) {
  return (
    <p className="px-3 pt-2 pb-1 text-micro font-semibold tracking-wide text-ink-faint uppercase">
      {children}
    </p>
  );
}
